import { RailReveal } from "@/components/rail-kit/RailReveal";
import { destinationFor, droppableId, type DroppableData } from "@/dnd/ids";
import { cnTransferDropTargetSurface } from "@/dnd/transferDropTargetStyles";
import { useTransferDropHighlight } from "@/dnd/useTransferDropHighlight";
import { RailDisclosureIcon } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailChevron";
import {
  RailRowMenu,
  railRowMenuProps,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRowMenu";
import { useRailExpansion } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailExpansion";
import {
  useRailRowContextMenu,
  type RailRowMenuController,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import {
  ObjectActionMenu,
  ObjectActionMenuCloseProvider,
  useObjectActions,
  type ObjectActionHandlers,
} from "@/object-actions";
import { useAttachCanvasFolderTransferSubmenu } from "@/object-actions/transfer/useCanvasFolderTransferSubmenu";
import { buildResourceFolderKind } from "@/pages/workspaces/components/WorkspaceResourceFolderCardItem";
import type { Canvas, Folder } from "@cohesive/api-client";
import { useDroppable } from "@dnd-kit/core";
import { Folder as FolderIcon, FolderOpen } from "lucide-react";
import { useMemo } from "react";

/** Actions surfaced on a folder row's kebab menu. */
export interface CanvasFolderActions {
  /** Navigate to the folder's view on the workspace detail page. */
  onOpen: (folder: Folder) => void;
  onRename: (folder: Folder) => void;
  onDelete: (folder: Folder) => void;
  onNewSubfolder: (parentFolderId: string) => void;
  /** Create the folder's resource kind (a canvas / a chat) directly inside it. */
  onNewResource: (folderId: string) => void;
}

/**
 * Three-dot actions menu for canvas folder rows — registry-driven (kind
 * `resource-folder:*`, surface `left-rail`), the same manifest the
 * workspace-detail folder cards render, so the rail and the main view can't
 * drift. Move to opens the nested destination picker; its subtree — the
 * descendant folders and the canvases filed in them — travels server-side.
 */
function CanvasFolderMenu({
  controller,
  folder,
  folders,
  actions: folderActions,
}: {
  controller: RailRowMenuController;
  folder: Folder;
  folders: Folder[];
  actions: CanvasFolderActions;
}) {
  const kind = useMemo(() => buildResourceFolderKind(folder), [folder]);
  const isCanvasFolder = folder.resource_type === "canvas";

  const handlers = useMemo(
    (): ObjectActionHandlers => ({
      open: () => folderActions.onOpen(folder),
      "new-canvas": isCanvasFolder
        ? () =>
            window.setTimeout(() => folderActions.onNewResource(folder.id), 0)
        : undefined,
      "new-chat": !isCanvasFolder
        ? () =>
            window.setTimeout(() => folderActions.onNewResource(folder.id), 0)
        : undefined,
      "new-folder": () =>
        window.setTimeout(() => folderActions.onNewSubfolder(folder.id), 0),
      rename: () => window.setTimeout(() => folderActions.onRename(folder), 0),
      delete: () => window.setTimeout(() => folderActions.onDelete(folder), 0),
      // Submenu-only: the destination picker is the interaction; the handler
      // exists so the resolver doesn't drop the action.
      move: () => {},
    }),
    [folder, folderActions, isCanvasFolder],
  );

  const { actions: baseActions } = useObjectActions({
    kind,
    surface: "left-rail",
    enabled: controller.open,
    handlers,
  });
  const actions = useAttachCanvasFolderTransferSubmenu(
    baseActions,
    folder,
    folders,
  );

  return (
    <RailRowMenu {...railRowMenuProps(controller)} label={folder.name}>
      <ObjectActionMenuCloseProvider value={() => controller.setOpen(false)}>
        <ObjectActionMenu actions={actions} kind={kind} name={folder.name} />
      </ObjectActionMenuCloseProvider>
    </RailRowMenu>
  );
}

/**
 * Drop target for a canvas folder (or the workspace root when `folderId` is
 * null). Dropping a canvas card here re-files it — a move (§5.4: the rail is an
 * organizing surface). Uses the shared transfer drop-target highlight, same as
 * [`FilesRailDropTarget`].
 */
export function CanvasFolderDropTarget({
  workspaceId,
  folderId,
  children,
  className,
  dropId: dropIdOverride,
}: {
  workspaceId: string;
  folderId: string | null;
  children: React.ReactNode;
  className?: string;
  /** Distinct droppable id for off-rail mounts (e.g. detail-page folder
   *  cards) — dnd-kit ids must be unique, and the rail may show the same
   *  folder at the same time. Destination semantics are unchanged. */
  dropId?: string;
}) {
  const dropId =
    dropIdOverride ?? droppableId.railCanvasFolder(workspaceId, folderId);

  const droppableData = useMemo(
    (): DroppableData => ({
      surface: "rail-canvas-folder",
      destination: destinationFor("rail-canvas-folder", {
        workspaceId,
        folderId,
      }),
    }),
    [workspaceId, folderId],
  );

  const droppable = useDroppable({ id: dropId, data: droppableData });
  // Organizing surface: active for every drag. A canvas shows a live "Move"
  // ring; a file/chat shows the grayed "can't move here" ring — never inert, so
  // the user always gets clear feedback over the Canvases section.
  const { isOver, disallowed } = useTransferDropHighlight(
    dropId,
    droppableData.destination,
  );

  return (
    <div
      ref={droppable.setNodeRef}
      className={cnTransferDropTargetSurface({ isOver, disallowed, className })}
    >
      {children}
    </div>
  );
}

/**
 * Expandable canvas folder row. Mirrors [`FilesRailFolderRow`]: the header is a
 * drop target, leads with an always-visible disclosure chevron beside the
 * folder icon, and children render indented when open. The hover-reveal
 * group scopes to the header row only (`group/row`), so hovering a child row
 * never reveals this folder's kebab.
 */
function CanvasFolderRow({
  workspaceId,
  folder,
  folders,
  actions,
  children,
}: {
  workspaceId: string;
  folder: Folder;
  /** Every folder in the workspace — feeds the move-into-own-subtree guard. */
  folders: Folder[];
  actions: CanvasFolderActions;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useRailExpansion(`folder:${folder.id}`);
  const menu = useRailRowContextMenu();

  return (
    <div className="flex flex-col">
      {/* Only the header row is a drop target — not the expanded children — so
          dragging a canvas *out* of this folder isn't recaptured by it. */}
      <CanvasFolderDropTarget workspaceId={workspaceId} folderId={folder.id}>
        <div
          onContextMenu={menu.onContextMenu}
          className="group/row relative flex min-h-7 w-full items-center gap-2.5 rounded-md px-2 pr-8 hover:bg-n-05"
        >
          <button
            type="button"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            data-state={open ? "open" : "closed"}
            className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
          >
            <RailDisclosureIcon
              open={open}
              icon={open ? FolderOpen : FolderIcon}
            />
            <span className="flex-1 truncate text-base text-n-80">
              {folder.name}
            </span>
          </button>
          <CanvasFolderMenu
            controller={menu}
            folder={folder}
            folders={folders}
            actions={actions}
          />
        </div>
      </CanvasFolderDropTarget>
      <RailReveal open={open} contentClassName="flex flex-col pl-2">
        {children}
      </RailReveal>
    </div>
  );
}

/**
 * Recursive canvas folder tree for one workspace, mirroring [`FilesRailTree`]:
 * renders the folders whose parent is `parentFolderId` (each containing its own
 * subtree) then the canvases filed directly under `parentFolderId`.
 * `renderCanvasRow` is supplied by the workspace row so per-canvas
 * handlers/permissions live in one place.
 */
export function WorkspacesRailCanvasFolderTree({
  workspaceId,
  parentFolderId,
  folders,
  canvases,
  renderCanvasRow,
  folderActions,
}: {
  workspaceId: string;
  parentFolderId: string | null;
  folders: Folder[];
  canvases: Canvas[];
  renderCanvasRow: (canvas: Canvas) => React.ReactNode;
  folderActions: CanvasFolderActions;
}) {
  const childFolders = useMemo(
    () =>
      folders
        .filter((f) => (f.parent_folder_id ?? null) === parentFolderId)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [folders, parentFolderId],
  );

  const folderCanvases = useMemo(
    () => canvases.filter((c) => (c.folder_id ?? null) === parentFolderId),
    [canvases, parentFolderId],
  );

  return (
    <>
      {childFolders.map((folder) => (
        <CanvasFolderRow
          key={folder.id}
          workspaceId={workspaceId}
          folder={folder}
          folders={folders}
          actions={folderActions}
        >
          <WorkspacesRailCanvasFolderTree
            workspaceId={workspaceId}
            parentFolderId={folder.id}
            folders={folders}
            canvases={canvases}
            renderCanvasRow={renderCanvasRow}
            folderActions={folderActions}
          />
        </CanvasFolderRow>
      ))}
      {folderCanvases.map((canvas) => renderCanvasRow(canvas))}
    </>
  );
}
